import { WHATSAPP_GLYPH } from '@/components/WhatsAppIcon';
import { SOCIAL_PLATFORMS, type SocialPlatform } from '@/features/storefront/sections/social';

/**
 * The official mark of each network, as one path on a 24x24 grid.
 *
 * **Why these are drawn here rather than imported.** The icon set this app uses
 * everywhere else is Lucide, which is an interface set: it has a `youtube` and
 * a `facebook`, both drawn as outlines in Lucide's own hand, and it has no
 * Pinterest and no WhatsApp at all. A row of icons where five are somebody's
 * stylised interpretation and two are missing is not "the platform's official
 * icon" - it is a row a shopper has to squint at. These are the marks the
 * networks publish, in one visual family, filled rather than stroked, so seven
 * of them standing in a line read as seven logos and not as seven drawings.
 *
 * **One path, `currentColor`, `fill-rule: evenodd`.** That is what lets the
 * renderer colour a mark by setting `color` on the anchor around it - the
 * theme's ink, the theme's background knocked out of a brand disc, or the
 * network's own colour - without a second copy of every glyph per style.
 *
 * Trademarks stay their owners'. A shop linking to its own profile with the
 * network's mark is the use those brand guidelines are written for, which is
 * also why nothing here is recoloured into a shop's palette by default and why
 * none of them can be edited.
 */
const GLYPHS: Record<Exclude<SocialPlatform, 'custom'>, string> = {
  youtube:
    'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  facebook:
    'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  linkedin:
    'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  x: 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.933zm-1.291 19.494h2.039L6.486 3.24H4.298l13.312 17.407z',
  instagram:
    'M12 0C8.741 0 8.333.014 7.053.072 5.775.132 4.905.333 4.14.63a5.877 5.877 0 0 0-2.126 1.384A5.877 5.877 0 0 0 .63 4.14C.333 4.905.131 5.775.072 7.053.014 8.333 0 8.741 0 12s.014 3.667.072 4.947c.06 1.277.261 2.148.558 2.913a5.877 5.877 0 0 0 1.384 2.126 5.877 5.877 0 0 0 2.126 1.384c.765.297 1.635.499 2.913.558C8.333 23.986 8.741 24 12 24s3.667-.014 4.947-.072c1.277-.06 2.148-.261 2.913-.558a5.877 5.877 0 0 0 2.126-1.384 5.877 5.877 0 0 0 1.384-2.126c.297-.765.499-1.636.558-2.913.058-1.28.072-1.688.072-4.947s-.014-3.667-.072-4.947c-.06-1.277-.261-2.148-.558-2.913a5.877 5.877 0 0 0-1.384-2.126A5.877 5.877 0 0 0 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.014 15.259 0 12 0zm0 2.163c3.204 0 3.584.012 4.85.07 1.17.053 1.805.249 2.227.413.56.218.96.478 1.38.898.42.42.68.82.898 1.38.164.423.36 1.058.413 2.228.058 1.265.07 1.645.07 4.849 0 3.204-.012 3.584-.07 4.85-.053 1.17-.249 1.805-.413 2.227-.218.56-.478.96-.898 1.38-.42.42-.82.68-1.38.898-.422.164-1.057.36-2.227.413-1.266.058-1.646.07-4.85.07-3.204 0-3.584-.012-4.85-.07-1.17-.053-1.805-.249-2.227-.413a3.712 3.712 0 0 1-1.38-.898 3.712 3.712 0 0 1-.898-1.38c-.164-.422-.36-1.057-.413-2.227-.058-1.266-.07-1.646-.07-4.85 0-3.204.012-3.584.07-4.85.053-1.17.249-1.805.413-2.227.218-.56.478-.96.898-1.38.42-.42.82-.68 1.38-.898.422-.164 1.057-.36 2.227-.413 1.266-.058 1.646-.07 4.85-.07zm0 3.678a6.159 6.159 0 1 0 0 12.318 6.159 6.159 0 0 0 0-12.318zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm7.846-10.405a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0z',
  pinterest:
    'M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.024 0 1.518.769 1.518 1.69 0 1.03-.655 2.569-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.208 0 1.031.397 2.137.893 2.738a.36.36 0 0 1 .083.345c-.091.379-.293 1.194-.333 1.361-.052.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146A12 12 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z',
  // The one mark the rest of the app needs too, so it lives beside them.
  whatsapp: WHATSAPP_GLYPH,
};

/**
 * The mark for a network we do not ship a logo for.
 *
 * A link, drawn in the same weight as the rest, rather than a wrong logo or a
 * blank square. Two strokes rather than one filled path, which is why it is
 * kept apart from `GLYPHS` - it is the one icon in the row that is an
 * interface icon, because that is what it honestly is.
 */
function CustomGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

export interface SocialIconProps {
  platform: SocialPlatform;
  /** Edge length in pixels. Square by construction - every mark is on a 24 grid. */
  size: number;
}

/**
 * One network's mark, in the current text colour.
 *
 * Always `aria-hidden`. The icon is never the accessible name of anything: the
 * anchor around it carries that, because "Instagram" is what a screen reader
 * should announce whether the mark loaded or not.
 */
export function SocialIcon({ platform, size }: SocialIconProps) {
  if (platform === 'custom') return <CustomGlyph size={size} />;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d={GLYPHS[platform]} />
    </svg>
  );
}

/** Every platform has a mark. Asserted here so a new one cannot ship without one. */
export const SOCIAL_ICONS_COMPLETE = SOCIAL_PLATFORMS.every(
  (platform) => platform === 'custom' || platform in GLYPHS,
);
